import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';

import { DisplayModes } from 'services/webedi/types';
import { ColumnTypesInternal, WebEDIRowData } from 'support/types/webediTable';
import { DataTable, DataTableHandle, useGenerateWebEDIColumns } from 'components/DataTable';
import { useDataTableContext } from 'components/DataTable/contexts/DataTableContext';

import { ProductMasterdataMode } from '../../hooks/useProductMasterdataMode';
import { buildProductMasterdataColumnsConfig } from '../../helpers/productColumns';
import { PRODUCT_KEPT_FIELD, PRODUCTS_DATA_PREFIX } from '../../helpers/productRows';
import { productLogColumn } from '../ProductLogColumn/productLogColumn';

export type Props = {
  mode: ProductMasterdataMode;
  emptyState?: ReactNode;
  footerMenu?: ReactNode;
  /** Disables the remove buttons and inputs while a save is in flight. */
  isSubmitting?: boolean;
  /** Lets a caller outside the table (e.g. the add-product button) scroll to a given row. */
  tableRef?: React.Ref<DataTableHandle>;
};

/**
 * Renders inside a `DataTableProvider name={PRODUCTS_DATA_PREFIX}` — it reads its row data from
 * `useDataTableContext()` rather than from the query response directly, so edits go through the
 * same debounced-snapshot/reshape machinery as WebEDI document tables.
 */
export const ProductMasterdataTable = ({ mode, emptyState, footerMenu, isSubmitting, tableRef }: Props) => {
  const { t } = useTranslation();
  const { fields } = useDataTableContext();
  const columnHelper = useMemo(() => createColumnHelper<WebEDIRowData>(), []);

  const config = useMemo(() => buildProductMasterdataColumnsConfig(t), [t]);
  // Rows can be removed only while editing. A saved product is only *marked* (`kept` goes to 0,
  // and its remove button turns into Restore), exactly like a document line whose quantity is set
  // to 0, and reaches the server on Save; a product the user just added is dropped outright.
  const generatedColumns = useGenerateWebEDIColumns({
    config,
    displayMode: mode,
    deletableRows: mode === DisplayModes.edit,
    deleteQuantityField: PRODUCT_KEPT_FIELD,
  });

  // The remove button leads the row (where a document's lines put it), so Log goes right after it
  // rather than in front of it.
  const columns = useMemo(() => {
    const logColumn = productLogColumn(columnHelper);
    const [first, ...rest] = generatedColumns;
    return first?.id === ColumnTypesInternal.procuros_delete
      ? [first, logColumn, ...rest]
      : [logColumn, ...generatedColumns];
  }, [columnHelper, generatedColumns]);

  // Referentially stable, or `memoizeRows` has nothing to compare and every row re-renders anyway.
  const meta = useMemo(() => ({ dataPrefix: PRODUCTS_DATA_PREFIX, mode, isSubmitting }), [mode, isSubmitting]);

  return (
    <DataTable
      ref={tableRef}
      key={mode}
      meta={meta}
      // Adding or removing a product re-rendered every row. Its cells take their values from the
      // form, so a row with unchanged data can be skipped.
      memoizeRows
      columns={columns}
      data={fields}
      // View mode keeps every loaded row mounted (`plain`), as infinite loading is what bounds
      // their number. Edit mode has no infinite loading, so it falls back to `auto`, which lets
      // large catalogues virtualize instead of mounting every row's ~8 registered inputs at once.
      dataRenderMode={mode === DisplayModes.view ? 'plain' : 'auto'}
      // The table lives in a flex column (see ProductMasterdata.tsx) — filling that column's
      // remaining height, instead of a fixed viewport-relative height, is what keeps the whole page
      // from needing its own scroll on top of the table's, in both modes. The footer (view mode's
      // infinite-scroll sentinel) then sits inside the table's scroll box, so it only fires once
      // the user scrolls to the end of the loaded rows.
      fillAvailableHeight
      enableGlobalSearch={false}
      showCellBorders={mode === DisplayModes.edit}
      emptyState={emptyState}
      footerMenu={footerMenu}
    />
  );
};
